import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Home, BarChart2, LogOut, User, Plus, ChevronRight, Pencil, Shield, Cpu } from 'lucide-react';
import { Drawer, DrawerTrigger, DrawerContent } from './ui/drawer';
import { cn } from '../lib/utils';
import { useAuth } from '../features/auth/contexts/AuthContext';
import crcEvansLogo from '../assets/crc-evans-logo.png';
import { Button } from './ui/button';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  onCreateClick?: () => void;
  onEditClick?: () => void;
  showBackNavigation?: boolean;
  mode?: 'create' | 'edit' | 'default';
  onBack?: () => void;
  setShowBackNavigation?: (showBackNavigation: boolean) => void;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  isActive?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  title = "Projects",
  subtitle ,
  onCreateClick,
  onEditClick,
  showBackNavigation = false,
  onBack,
  setShowBackNavigation
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'default' | 'edit'>('default');
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const navItems: NavItem[] = [
    { label: 'Projects', icon: <Home className="h-5 w-5" />, href: '/Projects', isActive: location.pathname === '/Projects' },
    { label: 'Analysis', icon: <BarChart2 className="h-5 w-5" />, href: '/Analysis', isActive: location.pathname === '/Analysis' },
    { label: 'Devices', icon: <Cpu className="h-5 w-5" />, href: '/Devices', isActive: location.pathname === '/Devices' },
    { label: 'Admin', icon: <Shield className="h-5 w-5" />, href: '/Admin', isActive: location.pathname === '/Admin' },
  ];
  useEffect(() => {
    if(showBackNavigation){
      setMode('edit');
    }
    else{
      setMode('default');
    }
  },[showBackNavigation])

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsDrawerOpen(false);
  };

  // Determine the title based on the current path
  const pageTitle = location.pathname === '/Projects' 
    ? 'Projects' 
    : location.pathname === '/Devices'
      ? 'Devices'
      : location.pathname === '/Analysis'
        ? 'Analysis'
        : location.pathname === '/Admin'
          ? 'Admin'
          : title;

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Decorative background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[#e04530]/5 mix-blend-multiply"></div>
        <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-[#e04530]/10 to-transparent"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #e04530 1px, transparent 0)`,
          backgroundSize: '40px 40px',
          opacity: 0.05
        }}></div>
      </div>

      <div className="relative z-10" >
        {/* Header Bar */}
        <header className="fixed top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur-sm border-b border-gray-200/50 shadow-sm z-50">
          <div className="h-full px-4 flex items-center gap-4" >
            <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
              <DrawerTrigger asChild>
                <Button 
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[10px] justify-center">
                  <Menu className="h-6 w-6 text-[#e04530]" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="bg-white border-r border-gray-200 max-w-[280px]" style={{width: '230px'}}>
                <div className="flex flex-col h-full">
                  {/* Logo Section */}
                  <div className="flex justify-center items-center py-1.5 border-b border-gray-100">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base font-bold text-[#E03540]">DATA</span>
                      <div className="w-5 h-5 relative group hover:scale-105 transition-transform duration-300">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#e04530] to-[#ed8b00] rounded-lg transform rotate-45 shadow-sm"></div>
                        <div className="absolute inset-[1.5px] bg-white rounded-lg transform rotate-45"></div>
                        <div className="absolute inset-[1.5px] bg-gradient-to-r from-[#e04530] to-[#ed8b00] rounded-lg transform rotate-45 opacity-90"></div>
                        <div className="absolute inset-0 flex items-center justify-center text-white text-[8px] font-black group-hover:animate-[spin_1s_linear_infinite]">
                          360
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* User Profile Section */}
                  <div className="border-b border-gray-100">
                    <div className="flex flex-col items-center py-3">
                      <div className="group">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#e04530] to-[#ed8b00] p-[2px] transition-transform duration-300 ease-out group-hover:scale-105">
                          <div className="w-full h-full rounded-full bg-white flex items-center justify-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100"></div>
                            <User className="h-7 w-7 text-[#e04530] relative z-10 transition-transform duration-300 ease-out group-hover:scale-110" />
                          </div>
                        </div>
                      </div>
                      <h2 className="text-sm font-semibold text-gray-800 mt-1.5">John Doe</h2>
                      <p className="text-xs text-gray-500 -mt-0.5">Project Manager</p>
                    </div>
                  </div>

                  {/* Navigation Items */}
                  <nav className="flex-1 px-2 py-1.5">
                    {navItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-lg mb-1 transition-all duration-300 transform hover:translate-x-2 group",
                          item.isActive
                            ? "bg-gradient-to-r from-[#e04530] to-[#ed8b00] text-white shadow-md"
                            : "hover:bg-gray-50 text-gray-700"
                        )}
                        onClick={() => {
                          setIsDrawerOpen(false);
                          if (onBack) {
                            onBack();
                            navigate('/Projects');
                          }
                          setShowBackNavigation?.(false);
                        }}
                      >
                        <span className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
                          {item.icon}
                        </span>
                        <span className="font-medium text-sm">{item.label}</span>
                      </Link>
                    ))}
                  </nav>

                  <div className="mt-auto">
                    {/* Logout Button */}
                    <div className="px-2">
                      <button
                        className="flex items-center gap-2 px-3 py-2 text-[#E03540] hover:bg-gray-50 rounded-lg mb-2 transition-all duration-300 transform hover:translate-x-2 w-full group"
                        onClick={handleLogout}
                      >
                        <span className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
                          <LogOut className="h-4 w-4" />
                        </span>
                        <span className="font-medium text-sm">Logout</span>
                      </button>
                    </div>

                    {/* CRC Evans Logo */}
                    <div className="px-4 py-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-400 text-center mb-2 uppercase tracking-wider">Powered by</p>
                      <img 
                        src={crcEvansLogo} 
                        alt="CRC Evans Logo" 
                        className="h-5 w-auto mx-auto object-contain"
                      />
                    </div>
                  </div>
                </div>
              </DrawerContent>
            </Drawer>

            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#e04530] to-[#ed8b00] text-transparent bg-clip-text">
                {showBackNavigation  ? (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={async() => {
                        if(onBack){
                          onBack();
                          navigate('/Projects');
                        }
                      }}
                      className="flex items-center gap-2 group"
                      title="Go back to projects"
                    >
                      {/* <ChevronRight className="h-6 w-6 rotate-180 group-hover:text-[#e04530]" /> */}
                      <span className="group-hover:text-[#e04530]">Projects</span>
                    </button>
                    <ChevronRight className="h-6 w-6 text-gray-400" />
                    <span>{subtitle}</span>
                  </div>
                ) : (
                  pageTitle
                )}
              </h1>
            </div>

            <div className="ml-auto">
              {!showBackNavigation && onCreateClick && (
                <button
                  onClick={onCreateClick}
                  className="flex items-center gap-2 bg-gradient-to-r from-[#e04530] to-[#ed8b00] text-white px-4 py-2 rounded-lg hover:shadow-md transition-all duration-300 hover:scale-105"
                >
                  <Plus className="h-5 w-5" />
                  Create Project
                </button>
              )}
              {showBackNavigation && onEditClick && (
                <button
                  onClick={onEditClick}
                  className="flex items-center gap-2 bg-gradient-to-r from-[#e04530] to-[#ed8b00] text-white px-4 py-2 rounded-lg hover:shadow-md transition-all duration-300 hover:scale-105"
                >
                  <Pencil className="h-5 w-5" />
                  Edit Project
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-20 px-2 pb-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 